import { Component, HostListener, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NavigationEnd, RouterLink, RouterOutlet } from "@angular/router";
import { filter } from "rxjs/operators";
import { FormsModule } from "@angular/forms";
import {
  AlertComponent,
  SidebarComponent,
  SidebarHeaderComponent,
  SidebarNavComponent,
  SidebarBrandComponent,
  SidebarFooterComponent,
  ShadowOnScrollDirective,
  OffcanvasComponent,
  OffcanvasBodyComponent,
  OffcanvasHeaderComponent,
  ButtonDirective,
  SidebarService,
} from "@coreui/angular";
import { IconDirective } from "@coreui/icons-angular";
import { navItems } from "../../_nav";
import { Router } from "@angular/router";
import { ProfileResolver } from "@app/resolvers/profile.resolver";
import { environment } from "../../../environments/environment";
import { RequestService } from "@app/services/request/request.service";
import { UserService } from "@app/services/user/user.service";
import { NotificationService } from "@app/services/notification/notification.service";
import { EchoService } from "@app/services/echo/echo.service";
import { ToastrService } from "ngx-toastr";
import { ColorService } from "@app/services/color/color.service";
import { SuperAdminService } from "@app/services/super-admin/super-admin.service";
import { NgScrollbar } from "ngx-scrollbar";
import { DefaultHeaderComponent } from "../default-header/default-header.component";

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

@Component({
    selector: "app-dashboard",
    templateUrl: "./default-layout.component.html",
    styleUrls: ["./default-layout.component.scss"],
    standalone: true,
    imports: [
    AlertComponent,
    ButtonDirective,
    CommonModule,
    DefaultHeaderComponent,
    FormsModule,
    IconDirective,
    NgScrollbar,
    OffcanvasComponent,
    OffcanvasBodyComponent,
    OffcanvasHeaderComponent,
    RouterOutlet,
    RouterLink,
    SidebarComponent,
    SidebarBrandComponent,
    SidebarFooterComponent,
    SidebarHeaderComponent,
    SidebarNavComponent,
    ShadowOnScrollDirective,
]
})
export class DefaultLayoutComponent implements OnDestroy {
  private readonly sidebarThemePalette: Record<string, Record<string, string>> = {
    red: { "--app-sidebar-accent": "#ef4444", "--cui-sidebar-color": "#ef4444", "--cui-sidebar-brand-color": "#ef4444" },
    green: { "--app-sidebar-accent": "#34d399", "--cui-sidebar-color": "#34d399", "--cui-sidebar-brand-color": "#34d399" },
    blue: { "--app-sidebar-accent": "#60a5fa", "--cui-sidebar-color": "#60a5fa", "--cui-sidebar-brand-color": "#60a5fa" },
    gray: { "--app-sidebar-accent": "#9ca3af", "--cui-sidebar-color": "#9ca3af", "--cui-sidebar-brand-color": "#9ca3af" },
    indigo: { "--app-sidebar-accent": "#818cf8", "--cui-sidebar-color": "#818cf8", "--cui-sidebar-brand-color": "#818cf8" },
    purple: { "--app-sidebar-accent": "#a78bfa", "--cui-sidebar-color": "#a78bfa", "--cui-sidebar-brand-color": "#a78bfa" },
    primary: { "--app-sidebar-accent": "#60a5fa", "--cui-sidebar-color": "#60a5fa", "--cui-sidebar-brand-color": "#60a5fa" },
  };

  public sidebarMinimized = false;
  // Below the md breakpoint the sidebar is a drawer opened from the header, not a column.
  public isMobile = window.innerWidth < 768;
  public asideVisible = false;
  public navItems: any[] = [];
  profile: any;
  logo = "";
  logoFailed = false;
  activeRequests: any;

  notifications: any[] = [];
  notCount = 0;

  constructor(
    private route: Router,
    public profileService: ProfileResolver,
    private requestService: RequestService,
    private userService: UserService,
    private notificationService: NotificationService,
    private echoService: EchoService,
    private toastrService: ToastrService,
    public colorService: ColorService,
    public superAdminService: SuperAdminService,
    private sidebarService: SidebarService
  ) {
    // On phones the drawer covers the page, so close it once a menu link is followed.
    this.route.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      if (this.isMobile) this.sidebarService.toggle({ visible: false, id: "sidebar1" });
    });
    echoService.initConnection();
    echoService.listenNotifications().listen(".server.notification", (data: any) => {
      this.notifications.push(data);
      this.notCount++;
    });
    echoService.listenStatus().listen(".status.message", (data: any) => {
      if (data.name == "step-2" || data.name == "step-3")
        toastrService.success("Your import has finished successfully.");
      if (data.name == "qb-cus-success")
        toastrService.success("The information has been sync with Quickbooks.");
      if (data.name == "qb-cus-failed")
        toastrService.error(
          "A problem occurred when trying to create the client in Quickbooks, please contact your administrator.",
          "",
          { timeOut: 5000 }
        );
    });
  }

  ngOnInit() {
    // Keep sidebar expanded on desktop to avoid hidden state persistence.
    this.sidebarMinimized = window.innerWidth < 1200;
    sessionStorage.setItem("sidebarMinimized", this.sidebarMinimized.toString());
    
    this.profile = this.profileService.profileInfo;
    this.profile.initials =
      this.profile.first_name.charAt(0).toUpperCase() +
      this.profile.last_name.charAt(0).toUpperCase();
    this.logo = this.buildLogoUrl(this.profile.logo);
    this.logoFailed = false;
    this.applyAccentColor();

    if (this.profileService.hasPermission("See notifications"))
      this.loadNotications();

    // Initialize navItems immediately with filtered data
    let navClone = JSON.parse(JSON.stringify(navItems));
    this.navItems = this.navigationPermissions(navClone);
    
    // Load active requests count if user is not technician and has permission
    if (!this.profileService.isTechnician() && this.profileService.hasPermission("See requests")) {
      this.getActiveRequets();
    }

    this.watchImpersonation();
  }

  ngOnDestroy() {
    // Removed automatic page reload to prevent sidebar from disappearing on navigation
    if (this.impersonationTimer) {
      clearInterval(this.impersonationTimer);
      this.impersonationTimer = null;
    }
  }

  navigationPermissions(navigationItems: any[]): any[] {
    const userPermissions = this.profileService.getPermissions();
    let navWithPermissions: any[] = [];

    let requestIndex = navigationItems.findIndex((x: any) => x.name == "Requests");
    if (requestIndex >= 0) {
      navigationItems[requestIndex].badge = {
        text: this.activeRequests,
        color: "secondary",
      };
    }

    let filteredItems = [...navigationItems];

    filteredItems = filteredItems.filter((item) => {
      if (!item.role) return true;
      if (item.role === "Super Admin") return this.profileService.isSuperAdmin();
      return true;
    });

    if (!this.profileService.adminCompany.addon_location) {
      filteredItems = filteredItems.filter(item => item.name !== "Locations");
    }
    if (!this.profileService.adminCompany.storage) {
      filteredItems = filteredItems.filter(item => item.name !== "Documents");
    }
    if (!this.profileService.adminCompany.tracking) {
      filteredItems = filteredItems.filter(item => item.name !== "GPS Location");
    }
    if (!this.profileService.adminCompany.route_builder) {
      filteredItems = filteredItems.filter(item => item.name !== "Route Builder");
    }

    if (this.profileService.isAdmin()) {
      filteredItems = filteredItems.filter(item => 
        item.name !== "My Account" && 
        item.name !== "Leads" && 
        item.name !== "Opportunities"
      );
    }

    if (this.profileService.isAdmin()) {
      return this.dropEmptySections(this.menuIconColors(filteredItems));
    }

    filteredItems.forEach((item) => {
      if (item.permission) {
        const hasPermission = userPermissions.some((permission) =>
          permission.name === item.permission
        );

        if (hasPermission) {
          navWithPermissions.push(item);
        }
      } else {
        navWithPermissions.push(item);
      }
    });

    return this.dropEmptySections(this.menuIconColors(navWithPermissions));
  }

  /**
   * Removes section headers that ended up with no navigable item under them, which happens once
   * permissions and addon flags have filtered the list down.
   */
  dropEmptySections(navItems: any[]): any[] {
    return navItems.filter((item, index) => {
      if (!item.title) return true;

      const next = navItems[index + 1];
      return !!next && !next.title;
    });
  }

  /**
   * The API returns the logo as a root-relative path (`/storage/logo_123.png`) while apiUrl ends
   * in a slash, so joining them naively produced `host//storage/...`. Most servers tolerate it,
   * but strict proxies and CDNs do not.
   */
  private buildLogoUrl(logoPath: string): string {
    if (!logoPath) return "assets/img/brand/field-tech-fitness-logo.png";

    return (
      environment.api.apiUrl.replace(/\/+$/, "") + "/" + logoPath.replace(/^\/+/, "")
    );
  }

  /** Falls back to an initials avatar when the company logo cannot be loaded. */
  onLogoError() {
    this.logoFailed = true;
  }

  get companyName(): string {
    return this.profileService.adminCompany?.name ?? "";
  }

  get companyInitials(): string {
    return this.companyName
      .split(/\s+/)
      .filter((word) => !!word)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  }

  toggleMinimize(e: boolean) {
    this.sidebarMinimized = typeof e === 'boolean' ? e : !this.sidebarMinimized;
  }

  toggleSidebar() {
    this.sidebarMinimized = !this.sidebarMinimized;
    sessionStorage.setItem("sidebarMinimized", this.sidebarMinimized.toString());
  }

  /** Countdown shown in the impersonation banner, e.g. "12:04". */
  impersonationCountdown = "";
  private impersonationTimer: any = null;

  /**
   * Keep the banner's remaining time current. The server enforces the real 30 minute limit; if
   * the countdown reaches zero the session is closed here too so the Super Admin is not left
   * clicking on a session the API will refuse.
   */
  private watchImpersonation() {
    if (!this.superAdminService.isImpersonating) return;

    this.attachPendingLocation();

    const tick = () => {
      const seconds = this.superAdminService.impersonationSecondsRemaining;

      if (seconds === null) return;

      if (seconds <= 0) {
        this.stopImpersonation();
        return;
      }

      const minutes = Math.floor(seconds / 60);
      this.impersonationCountdown = `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
    };

    // The server owns the real deadline, so it is asked once on load before the local clock is
    // trusted to count down from it.
    this.superAdminService.getImpersonationStatus().subscribe(
      (status) => {
        if (status?.impersonating) {
          this.superAdminService.syncImpersonationDeadline(status.seconds_remaining);
        }
        tick();
      },
      () => tick()
    );

    this.impersonationTimer = setInterval(tick, 1000);
  }

  private attachPendingLocation() {
    const pending = this.superAdminService.takePendingLocation();
    if (pending === undefined) return;

    const warnMissing = () =>
      this.superAdminService.geolocationPermission().then((state) =>
        this.toastrService.warning(
          state === "denied"
            ? "Location is blocked for this site, so this session will have no map. Allow it in the browser's site settings to record it."
            : "No location was captured, so this session will have no map.",
          "Recording without a location"
        )
      );

    const lookup = pending
      ? Promise.resolve(pending)
      : this.superAdminService.captureLocation();

    lookup.then((location) => {
      if (!location) {
        warnMissing();
        return;
      }

      this.superAdminService.attachImpersonationLocation(location).subscribe(
        () => {},
        () =>
          this.toastrService.warning(
            "The location was captured but could not be saved, so this session will have no map.",
            "Recording without a location"
          )
      );
    });
  }

  /**
   * Hand the session back to the Super Admin that started the impersonation.
   *
   * The token is revoked server-side first, so returning early does not leave a usable token
   * alive for the rest of the window. The reload is what clears the impersonated profile out of
   * every resolver and cached service.
   */
  stopImpersonation() {
    const restore = (landOn: string) => {
      this.superAdminService.restoreImpersonatorSession();
      window.location.href = landOn;
    };

    this.superAdminService.endImpersonationSession().subscribe(
      () => restore("/admin/users"),
      (error) => {
        // The Super Admin always gets their own session back, but a failed revoke must not
        // look like a successful one: the impersonation token stays usable until it expires,
        // so land on the panel where it can be ended explicitly.
        this.toastrService.warning(
          error?.message ||
            error?.error?.message ||
            "Could not revoke the impersonation token. End the session from Live sessions.",
          "The session may still be active"
        );
        restore("/admin/impersonation");
      }
    );
  }

  toggleAside() {
    this.asideVisible = !this.asideVisible;
  }

  getActiveRequets() {
    this.requestService.getActiveRequests().subscribe((response) => {
      this.activeRequests = response;
      let navClone = JSON.parse(JSON.stringify(navItems));
      this.navItems = this.navigationPermissions(navClone);
    });
  }

  loadNotications() {
    this.notificationService
      .getNotifications({ user_id: this.profileService.profileInfo.id })
      .subscribe((response) => {
        this.notifications = response;
        this.notifications.forEach((x) => {
          if (!x.readed) this.notCount++;
        });
      });
  }

  menuIconColors(navItems: any[]) {
    let colorGradient = 700;
    navItems.forEach((x: any) => {
      if (x.title) return; // section headers have no icon to colour

      colorGradient = colorGradient == 400 ? 700 : colorGradient - 100;
      x.class = `icon-${this.profileService.profileInfo.components_color}-${
        colorGradient - 100
      }`;
    });

    return navItems;
  }

  saveColor() {
    this.userService
      .changePassword({
        components_color: this.profileService.profileInfo.components_color,
      })
      .subscribe();
  }

  setColor(color: string) {
    this.profileService.profileInfo.components_color = color;
    this.applyAccentColor();
    let navClone = JSON.parse(JSON.stringify(this.navItems));
    this.navItems = this.menuIconColors(navClone);
    this.saveColor();
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event: any) {
    this.isMobile = event.target.innerWidth < 768;
    if (event.target.innerWidth < 1200) {
      this.sidebarMinimized = true;
    } else {
      this.sidebarMinimized = false;
    }
    sessionStorage.setItem("sidebarMinimized", this.sidebarMinimized.toString());
  }

  get availableColors() {
    return this.colorService.availableColors;
  }

  get sidebarThemeStyles() {
    return (
      this.sidebarThemePalette[this.profileService.color] ||
      this.sidebarThemePalette.primary
    );
  }

  /**
   * Publishes the chosen Theme Color as CSS variables on <html>, so the shared page styles
   * (src/scss/_page.scss) follow it: --ftf-theme-accent (+ -rgb) for accents, and
   * --ftf-theme-accent-strong (the 700 shade) for solid surfaces with white text.
   */
  applyAccentColor() {
    const option: any = this.colorService.availableColors.find(
      (color) => color.value === this.profileService.color
    );
    const accent = option?.color || "#2563eb";
    const strong = option?.strong || "#1d4ed8";
    const hex = accent.replace("#", "");
    const rgb = [0, 2, 4].map((i) => parseInt(hex.substring(i, i + 2), 16)).join(", ");

    const root = document.documentElement.style;
    root.setProperty("--ftf-theme-accent", accent);
    root.setProperty("--ftf-theme-accent-rgb", rgb);
    root.setProperty("--ftf-theme-accent-strong", strong);
  }

  get sidebarAccentColor() {
    return (
      this.colorService.availableColors.find(
        (color) => color.value === this.profileService.color
      )?.color || "#60a5fa"
    );
  }

  get planFree() {
    return this.profileService.isPlanFree;
  }
}
